import { el } from './dom.js';

/**
 * Floating panel anchored to a trigger.
 *
 * The shared base of the role, channel, emoji and colour pickers.
 * The old selectors were native `<select multiple>` elements: fixed height,
 * horizontal scrolling when a label overflowed, an invisible selection on a
 * dark background, and no search at all. This component fixes all four at the
 * source.
 */
export interface PopoverOptions {
  trigger: HTMLElement;
  /** Contents of the panel, built once on opening. */
  build: (close: () => void) => HTMLElement;
  /** Width of the panel; the trigger's by default. */
  width?: 'trigger' | 'auto';
  onOpen?: (panel: HTMLElement) => void;
}

export class Popover {
  private panel: HTMLElement | null = null;
  private readonly onDocumentClick: (event: MouseEvent) => void;
  private readonly onKeydown: (event: KeyboardEvent) => void;
  private readonly onReposition: () => void;

  constructor(private readonly options: PopoverOptions) {
    this.onDocumentClick = (event) => {
      const target = event.target as Node;
      if (this.panel && !this.panel.contains(target) && !options.trigger.contains(target)) {
        this.close();
      }
    };

    this.onKeydown = (event) => {
      if (event.key === 'Escape') {
        this.close();
        options.trigger.focus();
      }
    };

    // The panel is `position: fixed`: without repositioning, it would come away
    // from its trigger on the slightest scroll.
    this.onReposition = () => this.position();

    options.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggle();
    });
  }

  get isOpen(): boolean {
    return this.panel !== null;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    if (this.isOpen) return;

    /*
     * The panel is the flex container, and that is the fix.
     *
     * It used to be a plain box with a maximum height, and the list inside
     * asked for `max-h-full`. A percentage height resolves against a parent
     * with a *definite* height, and a box with only `max-height` has none — so
     * the wrapper grew to its content, the panel clipped it, and the list never
     * overflowed anything: nothing scrolled, and forty roles ended at the
     * fifteenth with no scrollbar to say so.
     *
     * A flex column with a maximum height does constrain its children, so the
     * list can be told to shrink (`min-h-0`) and scroll. `min-h-0` alone was
     * not enough, and shipping it taught me that.
     */
    const panel = el('div', {
      class:
        'bg-base-200 border-base-300 rounded-box fixed z-[100] flex flex-col border shadow-2xl overflow-hidden',
    });
    panel.append(this.options.build(() => this.close()));

    /*
     * A `<dialog>` opened through `showModal()` lives in the browser's *top
     * layer*: it sits above everything, whatever the `z-index`. A panel
     * attached to `document.body` therefore ended up hidden behind the editor -
     * making it impossible to pick an emoji or a role from a modal. We attach
     * it to the open dialog when there is one.
     */
    const host = this.options.trigger.closest('dialog[open]') ?? document.body;
    host.append(panel);
    this.panel = panel;
    this.options.trigger.setAttribute('aria-expanded', 'true');

    this.position();
    this.options.onOpen?.(panel);

    // Deferred: without this, the very click that opened the panel would close it.
    setTimeout(() => {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onReposition, true);
      window.addEventListener('resize', this.onReposition);
    }, 0);
  }

  close(): void {
    if (!this.panel) return;

    this.panel.remove();
    this.panel = null;
    this.options.trigger.setAttribute('aria-expanded', 'false');

    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onReposition, true);
    window.removeEventListener('resize', this.onReposition);
  }

  /**
   * Places the panel under the trigger, and keeps it there.
   *
   * It used to flip above whenever the panel did not fit whole below — which,
   * for a list that can be three hundred pixels tall, is most of a form. A
   * selector in the middle of a page opened upward, covering the field it
   * belonged to and the label above it, and the reader lost track of what they
   * were choosing for.
   *
   * A menu belongs under the thing it opens. So the panel is **shrunk to the
   * room available** rather than moved: it scrolls, which is what a long list
   * does anyway. Flipping is kept for the case it was written for — a trigger
   * genuinely near the bottom edge, where downward would leave a sliver.
   */
  private position(): void {
    const panel = this.panel;
    if (!panel) return;

    const anchor = this.options.trigger.getBoundingClientRect();
    const margin = 8;

    if (this.options.width !== 'auto') {
      panel.style.width = `${String(anchor.width)}px`;
    }

    /*
     * Below this, downward is not an option any more: a hundred and sixty
     * pixels is about four rows and a search field, and less than that is a
     * panel nobody can use without scrolling it into existence first.
     */
    const USABLE = 160;

    const spaceBelow = window.innerHeight - anchor.bottom - margin;
    const spaceAbove = anchor.top - margin;

    // Upward only when down is unusable *and* up is genuinely better.
    const openUpward = spaceBelow < USABLE && spaceAbove > spaceBelow;

    /*
     * The panel gives up its height instead of its place — but never grows
     * past the design cap. A selector on a tall screen would otherwise open a
     * six-hundred-pixel list, which is not a menu any more.
     */
    const CAP = 320;
    const room = Math.floor(openUpward ? spaceAbove : spaceBelow);
    panel.style.maxHeight = `${String(Math.min(CAP, Math.max(120, room)))}px`;

    const height = Math.min(panel.offsetHeight, openUpward ? spaceAbove : spaceBelow);

    panel.style.top = openUpward
      ? `${String(Math.max(margin, anchor.top - height - margin))}px`
      : `${String(anchor.bottom + margin)}px`;

    // Horizontal clamping: a panel wider than the space left on the right would
    // run off the screen on mobile.
    const width = panel.offsetWidth;
    const left = Math.min(anchor.left, window.innerWidth - width - margin);
    panel.style.left = `${String(Math.max(margin, left))}px`;
  }
}

/** The panels' standard search field. */
export function searchField(placeholder: string, onInput: (query: string) => void): HTMLElement {
  const wrapper = el('div', { class: 'border-base-300 border-b p-2' });

  const input = el('input', {
    class: 'input input-sm input-bordered bg-base-100 w-full',
    attrs: { type: 'search', placeholder, autocomplete: 'off' },
  });
  input.addEventListener('input', () => onInput(input.value.trim().toLowerCase()));

  // Prevents the panel from closing when clicking inside the field.
  input.addEventListener('click', (event) => event.stopPropagation());

  wrapper.append(input);
  return wrapper;
}
